import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Ship, Download } from 'lucide-react';
import { apiCall } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { getCloudFrontUrl } from '../../utils/s3Upload';

const SeaShipmentDetails = () => {
  const { blNumber } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchShipmentDetails();
  }, [blNumber, user?.id]);

  const fetchShipmentDetails = async () => {
    try {
      setLoading(true);
      const response = await apiCall(`/sea-export/user/${user.id}`, { method: 'GET' });
      const data = await response.json();

      if (response.ok && data.success) {
        const list = data.data || [];
        const found = list.find(
          (s) => (s.bl_number || s.blNumber) === blNumber
        );
        if (found) {
          setShipment(found);
        } else {
          setError('Shipment not found');
        }
      } else {
        setError('Failed to fetch shipment details');
      }
    } catch (err) {
      console.error('Error fetching sea shipment:', err);
      setError('An error occurred while fetching shipment details');
    } finally {
      setLoading(false);
    }
  };

  const normalizePdfList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [value];
      } catch {
        return [value];
      }
    }
    return [];
  };

  const openPdfs = (pdfValue) => {
    const list = normalizePdfList(pdfValue);
    if (!list.length) return;
    list.forEach((key) => {
      const url = key.startsWith('http') ? key : getCloudFrontUrl(key);
      window.open(url, '_blank');
    });
  };

  const countryToFlag = (countryName) => {
    if (!countryName) return '';
    const map = {
      India: '🇮🇳', USA: '🇺🇸', 'United States': '🇺🇸', UK: '🇬🇧',
      'United Kingdom': '🇬🇧', UAE: '🇦🇪', Germany: '🇩🇪', China: '🇨🇳',
      Japan: '🇯🇵', Australia: '🇦🇺', Canada: '🇨🇦', France: '🇫🇷',
      Italy: '🇮🇹', Singapore: '🇸🇬', Malaysia: '🇲🇾', Netherlands: '🇳🇱',
      Belgium: '🇧🇪', 'Sri Lanka': '🇱🇰', Bangladesh: '🇧🇩',
    };
    return map[countryName] || '🌍';
  };

  const formatFcValue = (fc, currency) => {
    if (!fc || fc === '-') return '-';
    return currency ? `${currency} ${fc}` : fc;
  };

  if (loading) {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading sea shipment details...</p>
        </div>
      </div>
    );
  }

  if (error || !shipment) {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Package className="mx-auto mb-4 text-red-500" size={48} />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {error || 'Shipment Not Found'}
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const bl = shipment.bl_number || shipment.blNumber || blNumber;
  const portCode = shipment.port_code || shipment.portCode || '-';
  const portOfLoading = shipment.port_of_loading || shipment.portOfLoading || '-';
  const portOfDischargeCode = shipment.port_of_discharge_code || shipment.portOfDischargeCode || '-';
  const portOfDischarge = shipment.port_of_discharge || shipment.portOfDischarge || '-';
  const country = shipment.country_of_final_destination || shipment.countryOfFinalDestination || '-';
  const mode = shipment.mode || 'FCL';

  const fields = [
    { label: 'B/L Number', value: bl },
    { label: 'S/B No.', value: shipment.sb_no || shipment.sbNo },
    { label: 'S/B Date', value: shipment.sb_date || shipment.sbDate },
    { label: 'Exporter Name', value: shipment.exporter_name || shipment.exporterName },
    { label: 'Consignee', value: shipment.consignee_name || shipment.consigneeName },
    { label: 'Buyer', value: shipment.buyer_name || shipment.buyerName },
    { label: 'Total Pkt', value: shipment.pkg ? `${shipment.pkg} Pkt` : '-' },
    { label: 'Gr. Weight', value: shipment.gwt ? `${shipment.gwt} Kg` : '-' },
    { label: 'FOB Value', value: shipment.fob_value || shipment.fobValue },
    { label: 'FC Value', value: formatFcValue(shipment.fc_value || shipment.fcValue, shipment.currency) },
    { label: 'Container No', value: (() => { const arr = normalizePdfList(shipment.container_no || shipment.containerNo); return arr.length ? arr.join(', ') : '-'; })() },
    { label: 'IGST', value: (shipment.igst_amt || shipment.igstAmt) ? `₹${shipment.igst_amt || shipment.igstAmt}` : '-' },
    { label: 'DBK', value: (shipment.dbk_claim || shipment.dbkClaim) ? `₹${shipment.dbk_claim || shipment.dbkClaim}` : '-' },
    { label: 'Rodtep', value: (shipment.rodtep_amt || shipment.rodtepAmt) ? `₹${shipment.rodtep_amt || shipment.rodtepAmt}` : '-' },
    { label: 'Rosctl', value: (shipment.rosctl_amt || shipment.rosctlAmt) ? `₹${shipment.rosctl_amt || shipment.rosctlAmt}` : '-' },
    { label: 'LEO Date', value: shipment.leo_date || shipment.leoDate },
    { label: 'Invoice No', value: shipment.inv_no || shipment.invNo },
    { label: 'Mode', value: mode },
  ];

  const hasBlPdf = !!(shipment.bl_pdf || shipment.blPdf);
  const hasLeoPdf = normalizePdfList(shipment.leo_pdf || shipment.leoPdf).length > 0;
  const hasInvPlPdf = normalizePdfList(shipment.inv_pl_pdf || shipment.invPlPdf).length > 0;
  const hasInvoicePdf = normalizePdfList(shipment.invoice_pdf || shipment.invoicePdf).length > 0;

  return (
    <div className="min-h-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Top Bar */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 sm:gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-3 sm:mb-4"
          >
            <ArrowLeft size={20} />
            <span className="text-sm sm:text-base">Back</span>
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Sea Shipment Details
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                B/L: {bl}
              </p>
            </div>

            {/* Download Buttons */}
            <div className="flex flex-wrap gap-2">
              {hasBlPdf && (
                <button
                  onClick={() => openPdfs(shipment.bl_pdf || shipment.blPdf)}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center gap-1.5 text-xs sm:text-sm"
                >
                  <Download size={14} />
                  BL
                </button>
              )}
              {hasLeoPdf && (
                <button
                  onClick={() => openPdfs(shipment.leo_pdf || shipment.leoPdf)}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1.5 text-xs sm:text-sm"
                >
                  <Download size={14} />
                  LEO
                </button>
              )}
              {hasInvPlPdf && (
                <button
                  onClick={() => openPdfs(shipment.inv_pl_pdf || shipment.invPlPdf)}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-1.5 text-xs sm:text-sm"
                >
                  <Download size={14} />
                  INV/PL
                </button>
              )}
              {hasInvoicePdf && (
                <button
                  onClick={() => openPdfs(shipment.invoice_pdf || shipment.invoicePdf)}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-1.5 text-xs sm:text-sm"
                >
                  <Download size={14} />
                  INVOICE
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
        {/* Route Banner */}
        <div className="bg-gradient-to-r from-cyan-500 via-teal-500 to-blue-500 dark:from-gray-700 dark:via-gray-650 dark:to-gray-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 relative overflow-hidden shadow-lg">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/20 rounded-full -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/20 rounded-full -ml-16 -mb-16"></div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 relative z-10">
            {/* From */}
            <div className="text-center">
              <p className="text-xs sm:text-sm text-white/90 mb-1">From</p>
              <h3 className="text-2xl sm:text-3xl font-bold text-white flex items-center justify-center gap-2">
                {portCode}
                <span className="text-2xl sm:text-3xl">{countryToFlag('India')}</span>
              </h3>
              <p className="text-xs sm:text-sm text-white/80 font-medium">{portOfLoading}</p>
            </div>

            {/* Ship Arrow */}
            <div className="flex-1 relative hidden sm:block">
              <div className="h-px bg-white/60 w-full"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-700 px-3 py-1 rounded-full border-2 border-white flex items-center gap-2 shadow-lg">
                <Ship size={18} className="text-cyan-600 dark:text-cyan-400 animate-pulse" />
                <span className="text-sm font-bold text-gray-900 dark:text-white">{mode}</span>
              </div>
            </div>

            {/* Mobile ship badge */}
            <div className="sm:hidden w-full flex justify-center">
              <div className="bg-white/20 px-3 py-1.5 rounded-full border border-white/40 flex items-center gap-2">
                <Ship size={14} className="text-white" />
                <span className="text-xs font-bold text-white">{mode}</span>
              </div>
            </div>

            {/* To */}
            <div className="text-center">
              <p className="text-xs sm:text-sm text-white/90 mb-1">To</p>
              <h3 className="text-2xl sm:text-3xl font-bold text-white flex items-center justify-center gap-2">
                <span className="text-2xl sm:text-3xl">{countryToFlag(country)}</span>
                {portOfDischargeCode}
              </h3>
              <p className="text-xs sm:text-sm text-white/80 font-medium">{portOfDischarge}, {country}</p>
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
            Shipment Information
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
            {fields.map((item, idx) => (
              <div
                key={idx}
                className="p-2.5 sm:p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all"
              >
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5 sm:mb-1 font-medium">
                  {item.label}
                </p>
                <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white break-words">
                  {item.value || '-'}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeaShipmentDetails;
